import {
    FreeTextDataResult,
    FreeTextSearchResult,
    SearchError,
    SearchResult,
    SearchResultType,
    SerialNumberSearchResult,
    SparePartListSearchResult,
    SparePartsDataResult,
    SparePartsSearchResult,
    TypeDesignationDataResult,
    TypeDesignationSearchResult,
} from './search.interface';

/**
 * Used to concat multiple search terms where needed (for UI purposes)
 */
export const SEARCH_TERM_SEPARATOR = ',';

export type SearchResultFilter<T extends SearchResult> = (result: SearchResult) => result is T;

export const isSerialNumberResult = (result: SearchResult): result is SerialNumberSearchResult =>
    !!result && result.type === SearchResultType.SERIALNUMBER;

export const isSparePartListResult = (result: SearchResult): result is SparePartListSearchResult =>
    !!result && result.type === SearchResultType.SPARE_PART_LIST;

export const isSparePartsResult = (result: SearchResult): result is SparePartsSearchResult =>
    !!result && result.type === SearchResultType.SPARE_PARTS;

export const isFreeTextResult = (result: SearchResult): result is FreeTextSearchResult =>
    !!result && result.type === SearchResultType.FREE_TEXT;

export const isTypeDesignationResult = (result: SearchResult): result is TypeDesignationSearchResult =>
    !!result && result.type === SearchResultType.TYPE_DESIGNATION;

export const isFreeTextDataResult = (result: SearchResult): result is FreeTextDataResult =>
    !!result && result.type === SearchResultType.FREE_TEXT_DATA;

export const isSparePartsDataResult = (result: SearchResult): result is SparePartsDataResult =>
    !!result && result.type === SearchResultType.SPARE_PARTS_DATA;

export const isTypeDesignationDataResult = (result: SearchResult): result is TypeDesignationDataResult =>
    !!result && result.type === SearchResultType.TYPE_DESIGNATION_DATA;

export const isSparePartsDataResultOrIsFreeTextDataResult = (
    result: SearchResult
): result is SparePartsDataResult | FreeTextDataResult =>
    isSparePartsDataResult(result) || isFreeTextDataResult(result);

export const needToCheckForOrderNumberItems = (searchError: SearchError | null) =>
    !!searchError && searchError.orderNumberItemsChecked === false;

/**
 * Create a function that checks if the value passed to it is either `null` or accepted by the given type guard.
 *
 * @param guard type guard to extend
 * @returns a function that returns whether or not the passed value matches the given guard or `null`
 */
export const isNullOr =
    <T>(guard: (arg: any) => arg is T): ((value: any) => value is T | null) =>
    (value: any): value is T | null =>
        value === null || guard(value);

/**
 * Split up search string into array of search terms
 *
 * @param searchStr Search string to split up
 * @returns array of search terms
 */
export const splitSearchString = (searchStr: string): string[] => searchStr.trim().split(/[,;\t\n]+/g);
